import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router';

import { ApiError } from '@shared/api/client';
import { Breadcrumb } from '@shared/chrome/Breadcrumb';
import { resolveErrorCopy } from '@shared/errors/catalog';
import { toArabicDigits } from '@shared/format';
import { Alert } from '@shared/ui/Alert';
import { ConfirmDialog, type ConfirmSubject } from '@shared/ui/dialog';
import { ErrorBanner } from '@shared/ui/ErrorBanner';

import { readImageGeometry } from '@features/content/_shared/upload';

import { isUneditableBody } from '../_shared/body';
import { deleteCopy, editorCopy } from '../_shared/copy';
import { editorialErrors } from '../_shared/errors';
import {
  useUpdatePost,
  useUploadPostArtwork,
  useDeletePost,
  useWriterTransition,
} from '../_shared/mutations';
import { PostEditor } from '../_shared/PostEditor';
import type { ArtworkFailure, ArtworkSlot } from '../_shared/PostEditor.Rail';
import { postKey, useArtworkPolicy, usePost, useRelatedCandidates, useTags } from '../_shared/queries';
import { isWriterEditable } from '../_shared/states';
import type { PostDraft } from '../_shared/types';
import { EditorialEditSkeleton } from './EditorialEdit.Skeleton';
import { copy } from './copy';

/**
 * The authoring surface — title, block body with embedded media, author, tags,
 * artwork, and attached titles.
 *
 * **Four breadcrumb levels, because the post is a destination.** This route
 * genuinely nests under the post's own page, and only real routes become
 * crumbs — skipping the post would hide the level that leads to it.
 *
 * **The state is shown, never picked**, and the page serves two of the five.
 * A post is editable by its writer in draft and in rejected and in no other
 * state; awaiting review it sits with the reviewer, and published and deleted
 * are managed from the post's page. Reaching this URL in one of those three
 * meets a stated notice and a door, not an empty form.
 *
 * **«ارفع للمراجعة» renders where the writer has something to hand over** —
 * the same two states — rather than being drawn always and refused on press.
 */
export function EditorialEditPage() {
  const { postId } = useParams<{ postId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const query = usePost(postId);
  const tags = useTags();
  const [relatedTerm, setRelatedTerm] = useState('');
  const related = useRelatedCandidates(relatedTerm, true);

  const updatePost = useUpdatePost();
  const uploadArtwork = useUploadPostArtwork();
  const artworkPolicy = useArtworkPolicy();
  const writerTransition = useWriterTransition();
  const deletePost = useDeletePost();

  const [deleting, setDeleting] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const [writeError, setWriteError] = useState<unknown>(null);
  /**
   * The artwork refusal, held apart from `writeError` on purpose.
   *
   * They render in two different places — this one beside the upload control in
   * the rail, `writeError` at the foot of the form — because they answer two
   * different actions, and a save failure drawn next to a file input is as
   * misplaced as an upload failure drawn below the buttons.
   */
  const [artworkError, setArtworkError] = useState<ArtworkFailure | null>(null);

  const post = query.data;
  const editable = post ? isWriterEditable(post.state) : false;

  const confirmSubject: ConfirmSubject | null = post
    ? {
        heading: deleteCopy.heading(post.title),
        body: deleteCopy.body,
        commitLabel: deleteCopy.commit,
        intent: 'danger',
      }
    : null;

  function save(draft: PostDraft, then: 'stay' | 'submit') {
    if (!post) return;
    setWriteError(null);

    updatePost.mutate(
      { postId: post.id, draft },
      {
        onSuccess: () => {
          if (then === 'stay') {
            setAnnouncement(copy.announce.saved);
            return;
          }
          // Save before submit, always: handing over a post whose last
          // paragraph never reached the server sends the reviewer a draft the
          // writer did not write.
          writerTransition.mutate(
            { postId: post.id, transition: 'submit' },
            {
              onSuccess: () => {
                setAnnouncement(copy.announce.submitted);
                void navigate(`/editorial/${post.id}`);
              },
              onError: setWriteError,
            },
          );
        },
        onError: setWriteError,
      },
    );
  }

  /**
   * Measure the chosen file HERE, and refuse it here when it is too small.
   *
   * **The geometry is read in the browser either way.** `useUploadPostArtwork`
   * has always called `readImageGeometry` before registering, because
   * `POST /artworks` declares `widthPx`/`heightPx` and `assets` re-reads them out
   * of the stored object at `:completeUpload`. So the console already knew the
   * file was below the floor before it sent anything — it just sent it anyway
   * and waited to be told.
   *
   * Refusing locally is not a second gate: `assets` still enforces the floor,
   * and this cannot make a refused file pass. What it changes is that the
   * operator reads their own two numbers against the required two, immediately,
   * instead of a sentence about a limit they must then go and look up.
   *
   * **The floor is only checked once it has been read.** While the policy query
   * is in flight there is nothing to compare against, and inventing a number to
   * compare against would refuse a legitimate file on a guess — so the request
   * goes, and the service answers with the refusal it owns.
   */
  async function pickArtwork(postId: string, file: File, slot: ArtworkSlot) {
    setWriteError(null);
    setArtworkError(null);

    const floorWidth = artworkPolicy.data?.min_width ?? null;
    const floorHeight = artworkPolicy.data?.min_height ?? null;

    if (floorWidth !== null && floorHeight !== null) {
      let geometry;
      try {
        geometry = await readImageGeometry(file);
      } catch {
        setArtworkError({ slot, message: editorCopy.art.unreadable });
        return;
      }

      if (geometry.widthPx < floorWidth || geometry.heightPx < floorHeight) {
        setArtworkError({
          slot,
          message: editorCopy.art.tooSmall(
            toArabicDigits(String(geometry.widthPx)),
            toArabicDigits(String(geometry.heightPx)),
            toArabicDigits(String(floorWidth)),
            toArabicDigits(String(floorHeight)),
          ),
        });
        return;
      }
    }

    uploadArtwork.mutate(
      { postId, file },
      { onError: (error) => setArtworkError({ slot, message: resolve(error).message }) },
    );
  }

  return (
    <>
      <Breadcrumb
        items={[
          { label: copy.crumbs.root, to: '/' },
          { label: copy.crumbs.list, to: '/editorial' },
          ...(post ? [{ label: post.title, to: `/editorial/${post.id}` }] : []),
          { label: copy.crumbs.current },
        ]}
      />

      <div className="mb-6 mt-3 grid gap-stack-tight">
        <h1 className="font-display text-h1 font-bold">{copy.head.title}</h1>
        <p className="mb-0 max-w-measure text-[1.0625rem] text-ink-muted">{copy.head.lede}</p>
      </div>

      <p role="status" aria-live="polite" className="sr-only">
        {announcement}
      </p>

      {query.isError ? (
        <ErrorBanner
          {...resolve(query.error)}
          onRetry={() => void queryClient.invalidateQueries({ queryKey: postKey(postId ?? '') })}
        />
      ) : null}

      {query.isPending ? <EditorialEditSkeleton /> : null}

      {post && !editable ? (
        <Alert kind="info" title={copy.notEditable.title}>
          <p className="mb-3">{copy.notEditable.body}</p>
          <Link to={`/editorial/${post.id}`} className="text-accent-text">
            {copy.notEditable.action}
          </Link>
        </Alert>
      ) : null}

      {post && editable ? (
        <PostEditor
          key={post.id}
          state={post.state}
          stateHint={post.state === 'REJECTED' ? copy.rejectedHint : copy.stateHint}
          initialTitle={post.title}
          initialBody={post.body}
          bodyUneditable={isUneditableBody(post)}
          authorName={post.author.displayName}
          tags={tags.data?.items ?? []}
          initialTagIds={post.tags.map((tag) => tag.id)}
          relatedCandidates={related.data?.items ?? post.relatedItems}
          onRelatedSearch={setRelatedTerm}
          thumbnailUrl={post.thumbnailUrl}
          heroArtworkUrl={post.heroArtworkUrl}
          artworkBusy={uploadArtwork.isPending}
          onArtworkFile={(file, slot) => {
            void pickArtwork(post.id, file, slot);
          }}
          artworkPolicy={artworkPolicy.data}
          artworkError={artworkError}
          initialRelatedIds={post.relatedItems.map((item) => item.id)}
          relatedLabels={Object.fromEntries(post.relatedItems.map((item) => [item.id, item.title]))}
          busy={updatePost.isPending || writerTransition.isPending}
          primaryLabel={editorCopy.actions.saveChanges}
          onSave={(draft) => save(draft, 'stay')}
          onSubmitForReview={(draft) => save(draft, 'submit')}
          onDelete={() => setDeleting(true)}
          banner={writeError ? <ErrorBanner {...resolve(writeError)} /> : undefined}
        />
      ) : null}

      <ConfirmDialog
        open={deleting}
        subject={confirmSubject}
        busy={deletePost.isPending}
        onConfirm={() => {
          if (!post) return;
          setDeleting(false);
          deletePost.mutate(post.id, {
            onSuccess: () => {
              setAnnouncement(copy.announce.deleted);
              void navigate('/editorial');
            },
            onError: setWriteError,
          });
        }}
        onCancel={() => setDeleting(false)}
      />
    </>
  );
}

function resolve(error: unknown) {
  const status = error instanceof ApiError ? error.status : 0;
  const code = error instanceof ApiError ? error.code : undefined;
  const resolved = resolveErrorCopy(code, status, editorialErrors);

  return {
    message: resolved.message,
    tone: resolved.tone === 'danger' ? ('danger' as const) : ('neutral' as const),
  };
}
